import {
  Skeleton,
  SkeletonLines,
  TableSkeleton,
} from "components/new-stuff/ui/Skeleton"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { useState } from "react"
import CustomCard from "components/new-stuff/ui/CustomCard"
import CrossBorderTransTable from "./transactions/component/Table"
import { IEmployeeWalletRes } from "./transactions/interface"
import { useAuthStore } from "stores"

import { walletBaseUrl } from "utils/newbaseurl"
import { useQuery } from "@tanstack/react-query"
import { walletAxiosInstance } from "utils/request"
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom"
import RequestAccount from "./modals/RequestAccount"
import { availableFlags } from "./sampleData"
import CurrencyCard from "./component/CurrencyCard"
import { IWalletBalance, IWalletPade } from "../interface/wallet"
import { Spin } from "antd"
import RequestCurCard from "./component/CustomCard"
import Rates from "./component/Rates"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"
import { Button, H2 } from "components"
import CompanyVerification from "../integration/CompanyVerification"
import { useGet } from "utils/useFetch"

export type IFlowSetupRes = IBaseRes<IFlowSetup>
export interface IFlowSetup {
  isPayScheduleSetup: boolean
  isAddressSetup: boolean
  isStatutoryInfoSetup: boolean
  isWalletSetup: boolean
  isPaygradeSetup: boolean
  isPaybandSetup: boolean
  isEmployeeSetup: boolean
  isContractorSetup: boolean
  reviewStatus?: null
  companyMessage?: null
  payMessage?: null
  teamMessage?: null
  payrollMessage?: null
}

/** A CurrencyCard's box, 250 by 156, while the balances load. */
const CurrencyCardSkeleton = () => (
  <div className="min-w-[250px] h-[156px] rounded-5px border bg-white pt-5 pb-[18px] pl-4 pr-3 flex flex-col justify-between">
    <div className="flex items-center gap-2">
      <Skeleton
        variant="circle"
        width="32px"
        height="32px"
      />
      <Skeleton width="56px" />
    </div>
    <Skeleton
      width="140px"
      height="24px"
    />
    <Skeleton width="90px" />
  </div>
)

/**
 * The balances dashboard, drawn while it is still unknown whether the
 * company's wallet is set up: the balance cards, the rates panel and recent
 * transactions in their real sizes.
 */
const WalletBalancesSkeleton = () => (
  <div
    className="flex flex-col w-full"
    aria-busy="true"
  >
    <Skeleton
      width="180px"
      height="20px"
    />
    <div className="mt-4 flex items-center gap-4 overflow-hidden min-h-[156px]">
      {Array.from({ length: 3 }, (_, i) => (
        <CurrencyCardSkeleton key={i} />
      ))}
    </div>
    <div className="mt-[31px] h-[159px] rounded-5px border bg-white p-5">
      <SkeletonLines lines={4} />
    </div>
    <div className="mt-[25px] rounded-5px border bg-white p-5">
      <TableSkeleton
        rows={4}
        columns={4}
      />
    </div>
  </div>
)

const Overview = () => {
  const { user } = useAuthStore((state) => state)
  const [activeTab, setActiveTab] = useState(0)

  const navigate = useNavigate()
  const walletId = user?.organisationWalletId
  const [hideDetails, setHideDetails] = useState(true)

  const [startDate, setStartDate] = useState(
    dayjs(new Date()).subtract(7, "day").format("YYYY/MM/DD") || ""
  )
  const [endDate, setEndDate] = useState(
    dayjs(new Date())?.format("YYYY/MM/DD") || ""
  )
  const [openRequestModal, setOpenRequestModal] = useState(false)
  const { data: isWalletSetup, isLoading } = useGet<IFlowSetupRes>({
    url: `/account/getcompanysetupinfo?company_id=${user?.currentCompanyId}&organisation_id=${user?.userOrganisationId}`,
    instanceKey: "newArchAxiosInstance",
  })

  const { isLoading: iswalletLoading, data: walletData } =
    useQuery<IWalletPade>({
      queryKey: [`${walletBaseUrl}/v1/wallets`],
      queryFn: async () => {
        const res = await walletAxiosInstance.get(`/v1/wallets/${walletId}`)
        return res.data
      },

      // keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
      onSuccess: (data: any) => {
        if (data.status === "success") {
          mixPanelEvent(`wallet-listing`)
        }
      },
    })

  const { isLoading: isTransactionsLoading, data: transactionsData } =
    useQuery<IEmployeeWalletRes>({
      queryKey: [
        `${walletBaseUrl}/v1/wallets/${walletId}/transactions?limit=0&startDate=${
          startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""
        }&endDate=${endDate ? dayjs(endDate).format("YYYY-MM-DD") : ""}`,
      ],
      queryFn: async () => {
        const res = await walletAxiosInstance.get(
          `/v1/wallets/${walletId}/transactions?limit=0&startDate=${
            startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""
          }&endDate=${endDate ? dayjs(endDate).format("YYYY-MM-DD") : ""}`
        )
        return res.data
      },

      // keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
      onSuccess: (data: any) => {
        mixPanelEvent(`wallet-transaction-listing`)
      },
    })

  if (activeTab === 1)
    return <CompanyVerification onSuccess={() => setActiveTab(0)} />

  return (
    <DashboardWrapper>
      <>
        {isLoading ? (
          // Set-up status is not known yet. Showing the set-up screen here is
          // what made a company with a wallet see "Welcome to your PaidHR
          // Wallet" first, then a skeleton, then its balances.
          <WalletBalancesSkeleton />
        ) : !isWalletSetup?.data?.isWalletSetup ? (
          <div className="block lg:flex lg:items-center justify-between bg-white rounded-md p-10">
            <img
              src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/8056e78f-d641-4fb3-a11c-4fa8e04e0b65.png"
              width={344}
              height={344}
            />
            <div className="font-circular flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <H2 className="font-bold">Welcome to your PaidHR Wallet</H2>
                <p className="text-[#0C1B3D80]">
                  Send, receive and make payments.
                </p>
              </div>
              <div className="flex flex-col gap-4 p-4 rounded-md bg-[#E8F0FE] mb-5">
                To activate your wallet, we’ll need a few details to verify your
                identity.This helps keep your account secure and complies with
                Central Bank of Nigeria regulations.
                <strong>The process takes less than 2 minutes.</strong>
              </div>
              <Button
                color="primary"
                style={{ width: "100%" }}
                onClick={() => setActiveTab(1)}
              >
                Proceed
              </Button>
            </div>
          </div>
        ) : (
          <div className=" flex flex-col w-full ">
            {/* HEADER */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className=" text-padeBlack font-bold font-circular text-xl">
                  Wallet Balances{" "}
                </h2>
                <button
                  className="flex"
                  onClick={() => setHideDetails(!hideDetails)}
                >
                  {hideDetails ? (
                    <EyeIcon className="text-black w-6 h-6 cursor-pointer" />
                  ) : (
                    <EyeSlashIcon className="text-black w-6 h-6 cursor-pointer" />
                  )}
                </button>
              </div>
              {/* <Button
            color="primary"
            onClick={() => setOpenRequestModal(true)}
          >
            Request Currency
          </Button> */}
            </div>
            {/* BALANCES */}
            <>
              <div className="mt-4 flex  justify-start  items-center max-w-full min-h-[156px] overflow-x-auto gap-4">
                {iswalletLoading
                  ? Array.from({ length: 3 }, (_, i) => (
                      <CurrencyCardSkeleton key={i} />
                    ))
                  : null}
                {walletData?.data?.balances &&
                  rearrangeBalances(walletData?.data?.balances)?.map(
                    (curr: any, index: any) => {
                      const flag = availableFlags.find(
                        (flag) => flag.code === curr.currency
                      )?.flag

                      return (
                        <CurrencyCard
                          key={index}
                          curr={curr}
                          flag={flag}
                          tag={curr?.currency === "USD" ? "Global Payout" : ""}
                          handleClick={() => {
                            // curr?.type === "default" &&
                            curr?.currency === "NGN"
                              ? navigate("/dashboard/wallets/overview")
                              : navigate(
                                  `/dashboard/wallets/cross-border/view`,
                                  {
                                    state: { currencyCode: curr.currency },
                                  }
                                )
                          }}
                          hideDetails={hideDetails}
                        />
                      )
                    }
                  )}
                <RequestCurCard />
              </div>
            </>
            {/* RATES */}
            <div className="mt-[31px] h-[159px]">
              <Rates />
            </div>
            {/* TRANSACTION */}
            <div className="mt-[25px]">
              <CustomCard
                title="Recent Transactions"
                ctaBtn={
                  <button
                    className="text-primary500 flex text-sm items-center font-avenir"
                    onClick={() => navigate("/dashboard/wallets/statements")}
                  >
                    See All
                  </button>
                }
              >
                <CrossBorderTransTable
                  transactionsData={transactionsData?.data?.docs?.slice(0, 4)}
                  loading={isTransactionsLoading}
                />
              </CustomCard>
            </div>
            <RequestAccount
              open={openRequestModal}
              onClose={() => setOpenRequestModal(false)}
            />
          </div>
        )}
      </>
    </DashboardWrapper>
  )
}
export function rearrangeBalances(balances: IWalletBalance[]) {
  const ngnBalance = balances.find((balance) => balance.currency === "NGN")
  const otherBalances = balances.filter((balance) => balance.currency !== "NGN")

  const newNgnBalance = {
    ...ngnBalance,
    // currency: ngnBalance?.currency,
    // balance: ngnBalance?.balance,
    // _id: "",
    // symbol: ngnBalance?.symbol,
    // type: "",
  }
  return [newNgnBalance, ...otherBalances]
}

export default Overview
